import { useEffect, useRef,useState } from 'react'

import { Button, toast } from '@heroui/react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useSearchParams } from 'react-router-dom'

import { authApi } from '@/api/auth.api'
import authBg from '@/assets/auth-bg.png'
import { Card, CardContent } from '@/components/ui/Card'

export default function VerifyEmailPage() {
  const { t } = useTranslation()
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const token = searchParams.get('token')

  const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading')
  const [errorMessage, setErrorMessage] = useState('')
  const hasVerified = useRef(false)

  useEffect(() => {
    if (hasVerified.current) return
    hasVerified.current = true

    const verifyToken = async () => {
      if (!token) {
        setStatus('error')
        setErrorMessage(t('auth.verify.invalid_token'))
        return
      }

      try {
        await authApi.verifyEmail({ token })
        setStatus('success')
        toast.success(t('auth.verify.success'))
      } catch (err) {
        setStatus('error')
        setErrorMessage((err as Error).message || t('auth.verify.error_desc'))
      }
    }

    verifyToken()
  }, [token, t])

  return (
    <div
      className="min-h-screen flex items-center justify-center bg-cover bg-center p-5 relative"
      style={{ backgroundImage: `url(${authBg})` }}
    >
      {/* Overlay */}
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm z-0 transition" />

      <div className="w-full max-w-md relative z-10 animate-in fade-in slide-in-from-bottom-4 duration-700">
        <div className="text-center mb-10">
          <div className="inline-flex w-14 h-14 rounded-2xl bg-blue-600 items-center justify-center text-white text-2xl font-bold mb-4 shadow-xl shadow-blue-500/20">
            W
          </div>
          <h1 className="text-3xl font-black text-white tracking-tight mb-2">
            {t('auth.company_name')}
          </h1>
          <p className="text-white/50 font-medium">
            {t('auth.system_name')}
          </p>
        </div>

        <Card className="bg-white/5 border-white/10 shadow-2xl">
          <CardContent className="p-8 text-center flex flex-col items-center gap-6">
            {status === 'loading' && (
              <>
                <svg className="animate-spin h-10 w-10 text-blue-500 mb-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                <div className="flex flex-col gap-2">
                  <h2 className="text-xl font-bold text-white">
                    {t('auth.verify.title')}
                  </h2>
                  <p className="text-white/60 text-sm">
                    {t('auth.verify.loading')}
                  </p>
                </div>
              </>
            )}

            {status === 'success' && (
              <>
                <div className="w-12 h-12 rounded-full bg-green-500/10 text-green-500 flex items-center justify-center mb-2">
                  <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <div className="flex flex-col gap-2">
                  <h2 className="text-xl font-bold text-white">
                    {t('auth.verify.success')}
                  </h2>
                  <p className="text-white/60 text-sm">
                    {t('auth.verify.success_desc')}
                  </p>
                </div>
                <Button
                  fullWidth
                  onClick={() => navigate('/login')}
                  className="mt-2"
                >
                  {t('auth.verify.go_to_login')}
                </Button>
              </>
            )}

            {status === 'error' && (
              <>
                <div className="w-12 h-12 rounded-full bg-red-500/10 text-red-500 flex items-center justify-center mb-2">
                  <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </div>
                <div className="flex flex-col gap-2">
                  <h2 className="text-xl font-bold text-white">
                    {t('auth.verify.error')}
                  </h2>
                  <p className="text-white/60 text-sm justify-center break-words max-w-full">
                    {errorMessage}
                  </p>
                </div>
                <Button
                  fullWidth
                  onClick={() => navigate('/login')}
                  className="mt-2"
                >
                  {t('auth.back_to_login')}
                </Button>
              </>
            )}
          </CardContent>
        </Card>

        <div className="mt-10 text-center">
          <p className="text-white/40 text-xs font-bold uppercase tracking-widest">
            &copy; 2026 {t('auth.company_name')}
          </p>
        </div>
      </div>
    </div>
  )
}
